import { DOM } from './state.js';
import { navigateTo } from './navigation.js';
import { stopAdminPanel } from './admin.js';

/**
 * Route registry — populated by registerRoute() calls in main.js.
 * @type {Array<{ pattern: string | RegExp, handler: function }>}
 */
const routes = [];

/**
 * Registers a route handler.
 *
 * @param {string | RegExp} pattern
 *   - String: matched exactly against the normalized pathname.
 *   - RegExp: tested against the pathname; named groups are passed as params.
 * @param {function(params?: Record<string, string>): void} handler
 */
export function registerRoute(pattern, handler) {
    routes.push({ pattern, handler });
}

/**
 * Hides all SPA view panels and resets shared layout state.
 * Called at the start of every route transition.
 */
function hideAllViews() {
    [
        DOM.viewWelcome,
        DOM.viewMenu,
        DOM.viewMenuSelection,
        DOM.viewGiftcards,
        DOM.viewEventos,
        DOM.viewReservas,
        DOM.viewAdmin,
        DOM.viewCart,
        DOM.viewCheckout,
        DOM.viewPedidos,
    ].forEach(v => { v?.classList.add('hidden'); });

    // Esconder no alcanza para el panel de staff: sus secciones pueden tener
    // estado de fondo (el poll de 30s de Fichaje, ex-kiosco standalone — AD8/
    // AD9), y el content mount no siempre se saca del DOM, así que sin este
    // teardown ese estado seguiría corriendo por el resto de la vida de la
    // pestaña. `stopAdminPanel` (js/admin.js) es idempotente y segura cuando
    // el panel nunca se montó; `handleAdmin` vuelve a arrancar lo que
    // corresponda al entrar.
    stopAdminPanel();

    DOM.macroTabs?.classList.add('hidden');
    // WhatsApp flotante: visible en TODAS las vistas públicas (antes salía solo
    // en el home). hideAllViews es el contexto público por defecto — lo mostramos
    // acá; handleAdmin lo vuelve a ocultar porque el panel es full-bleed.
    DOM.whatsappFloat?.classList.remove('hidden');
    if (DOM.sectionSubtitle) DOM.sectionSubtitle.textContent = '';
    if (DOM.mainFooter) DOM.mainFooter.classList.remove('hidden');

    // El panel de staff es full-bleed y vive fuera de #publicShell; por defecto
    // el wrapper público está visible. handleAdmin lo oculta cuando /staff o /admin se activa.
    DOM.publicShell?.classList.remove('hidden');
}

/**
 * Matches a pathname against a registered pattern.
 *
 * @param {string | RegExp} pattern
 * @param {string} path
 * @returns {{ params: Record<string, string> } | null}
 */
function matchRoute(pattern, path) {
    if (pattern instanceof RegExp) {
        const m = path.match(pattern);
        return m ? { params: m.groups ?? {} } : null;
    }
    // String: exact match
    return path === pattern ? { params: {} } : null;
}

/**
 * Normalizes the pathname: strips trailing slash except for root '/'.
 *
 * @param {string} raw
 * @returns {string}
 */
function normalizePath(raw) {
    return raw.length > 1 && raw.endsWith('/') ? raw.slice(0, -1) : raw;
}

/**
 * Main router — dispatches to the first matching registered handler.
 * Falls back to '/' (with Sentry notification) for unknown routes.
 */
export function router() {
    const path = normalizePath(window.location.pathname);

    hideAllViews();

    for (const { pattern, handler } of routes) {
        const match = matchRoute(pattern, path);
        if (match) {
            handler(match.params);
            return;
        }
    }

    // No handler matched — unknown route
    window.Sentry?.captureMessage(`Unknown route: ${path}`, 'info');
    navigateTo('/', true);
}

/**
 * Synchronizes the visibility of macro-section slides in the menu view.
 * Exported because menu.js calls it after a section transition.
 *
 * @param {string} sectionId
 */
export function syncMacroSections(sectionId) {
    const slides = document.querySelectorAll('.macro-section-slide');
    if (!DOM.products) return;

    slides.forEach(slide => {
        slide.style.display = (slide.id === `slide-${sectionId}`) ? 'block' : 'none';
        if (slide.id === `slide-${sectionId}`) slide.style.opacity = '1';
    });

    window.requestAnimationFrame(() => {
        window.scrollTo({ top: 0, behavior: 'instant' });
    });
}
